import { GET_AVAILABLE_RATE_GROUPS } from "@/actions/GET_AVAILABLE_RATE_GROUPS.ts";
import { GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE } from "@/actions/GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE.ts";
import {
  GetAvailableRateGroupsInput,
  GetAvailableRateGroupsResponse,
} from "@/actions/hooks/useLazyGetAvailableRateGroups.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useLazyQuery } from "@apollo/client";
import format from "date-fns/format";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Watches for language changes and re-fetches rate groups to update
 * cart items' translations (rateGroup name/description, amenities, ratePlan names).
 */
export const useRefreshCartTranslations = () => {
  const { i18n } = useTranslation();
  const prevLang = useRef(i18n.language);

  const cartAccommodationItems = useEngineStore((s) => s.cartAccommodationItems);
  const defaultDateRange = useEngineStore((s) => s.defaultDateRange);
  const updateCartItemsTranslations = useEngineStore((s) => s.updateCartItemsTranslations);
  const rootAttributes = useEngineStore((s) => s.rootAttributes);

  const useClassicRGQuery = rootAttributes[RootAttribute.UseClassicRatePlanDetails];
  const RGQuery = useClassicRGQuery ? GET_AVAILABLE_RATE_GROUPS_WITH_LOWEST_PRICE : GET_AVAILABLE_RATE_GROUPS;

  const [fetchRGs] = useLazyQuery<GetAvailableRateGroupsResponse, GetAvailableRateGroupsInput>(RGQuery, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (prevLang.current === i18n.language) return;
    prevLang.current = i18n.language;

    if (cartAccommodationItems.length === 0) return;
    if (!defaultDateRange[0] || !defaultDateRange[1]) return;

    const startDate = defaultDateRange[0];
    const endDate = defaultDateRange[1];

    if (typeof startDate === "string" || typeof endDate === "string") return;

    void fetchRGs({
      variables: {
        input: {
          checkIn: format(startDate, "yyyy-MM-dd"),
          checkOut: format(endDate, "yyyy-MM-dd"),
        },
        from: format(startDate, "yyyy-MM-dd"),
        until: format(endDate, "yyyy-MM-dd"),
      },
    }).then((result) => {
      if (result.data?.availableRateGroups) {
        updateCartItemsTranslations(result.data.availableRateGroups);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
};
